import { useEffect, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import api from "../lib/api";

const BADGE = "inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full min-w-[2rem]";

export default function TopicPicker({ section, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [numberQuery, setNumberQuery] = useState(null);
  const [exactExists, setExactExists] = useState(false);
  const [hi, setHi] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/topics", { params: { section, q } });
      setItems(data.items);
      setNumberQuery(data.number_query);
      setExactExists(data.exact_exists);
      setHi(0);
    }, 180);
    return () => clearTimeout(t);
  }, [q, open, section]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const options = [...items, { other: true }];

  const select = (opt) => {
    if (opt.other) onChange({ number: null, text: "", is_other: true });
    else onChange({ number: opt.number, text: opt.text, is_other: false });
    setOpen(false);
    setQ("");
  };

  const clear = () => onChange({ number: null, text: "", is_other: false });

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(options.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (options[hi]) select(options[hi]); }
  };

  const hiOpt = options[hi];

  // selected chip
  if (value && (value.is_other || value.number != null)) {
    const title = value.is_other ? "Other topic" : `#${value.number} · ${value.text.slice(0, 40)}`;
    return (
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => setExpanded((x) => !x)}
            className="inline-flex items-center gap-2 bg-[#F3E5D8] text-[#8B1A1A] rounded-full pl-1 pr-2 py-1 max-w-full" data-testid={`${testid}-chip`}>
            {!value.is_other && <span className={BADGE}>#{value.number}</span>}
            <span className="text-sm font-medium truncate max-w-[16rem]">{value.is_other ? "Other topic" : value.text}</span>
            <X size={15} onClick={(e) => { e.stopPropagation(); clear(); }} className="hover:text-[#671813]" />
          </button>
        </div>
        {expanded && !value.is_other && (
          <div className="mt-2 bg-[#F5F0E8] border-l-4 border-[#8B1A1A] rounded-lg p-3 text-sm text-[#210201]">{value.text}</div>
        )}
        {value.is_other && (
          <textarea rows={3} value={value.text} onChange={(e) => onChange({ ...value, text: e.target.value })}
            placeholder="Describe the topic you got, as closely as you remember it."
            className="mt-2 bg-[#E8DCC8] border border-[#E8DCC8] focus:border-[#8B1A1A] focus:bg-[#FFFDF9] focus:outline-none rounded-xl px-4 py-3 w-full" data-testid={`${testid}-other-text`} required />
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Type the topic number or a keyword"
          className="bg-[#E8DCC8] border border-[#E8DCC8] focus:border-[#8B1A1A] focus:bg-[#FFFDF9] focus:outline-none focus:ring-1 focus:ring-[#8B1A1A] rounded-xl px-4 py-3 w-full pr-9"
          data-testid={testid}
        />
        <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B1A1A] pointer-events-none" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#FFFDF9] border border-[#E8DCC8] rounded-xl shadow-lg max-h-[22rem] overflow-y-auto">
          {numberQuery != null && !exactExists && (
            <div className="px-4 py-2 text-sm text-[#671813] bg-[#F3E5D8]">No topic #{numberQuery} in this section</div>
          )}
          {options.map((opt, i) => opt.other ? (
            <button key="other" type="button" onMouseEnter={() => setHi(i)} onClick={() => select(opt)}
              className={`block w-full text-left px-4 py-3 border-t border-[#E8DCC8] italic text-[#8B1A1A] ${hi === i ? "bg-[#F3E5D8]" : ""}`}
              data-testid={`${testid}-other-option`}>
              Other — not in this list
            </button>
          ) : (
            <button key={opt.id} type="button" onMouseEnter={() => setHi(i)} onClick={() => select(opt)}
              className={`flex items-start gap-2 w-full text-left px-3 py-2.5 ${hi === i ? "bg-[#F3E5D8]" : ""}`}
              data-testid={`${testid}-option-${opt.number}`}>
              <span className={BADGE + " mt-0.5"}>#{opt.number}</span>
              <span className="text-sm text-[#210201] line-clamp-2">{opt.text}</span>
            </button>
          ))}
        </div>
      )}

      {open && hiOpt && !hiOpt.other && (
        <div className="mt-2 bg-[#F5F0E8] border-l-4 border-[#8B1A1A] rounded-lg p-3 text-sm text-[#210201]" data-testid={`${testid}-preview`}>
          <span className={BADGE + " mr-2"}>#{hiOpt.number}</span>{hiOpt.text}
        </div>
      )}
    </div>
  );
}
